import { useState, useEffect, useMemo } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  getCompareCycleTransactions,
  getCompareCycleCatalog,
  getCompareCycleConfig,
  setCompareCyclePrices,
  confirmCompareCyclePrices,
  getCompareCycleBidHistory,
  getCompareCycleLostPositions,
  getCompareCycleSelections,
  saveCompareCycleSelection,
  deleteCompareCycleSelection,
} from '@/services/api';
import {
  ShoppingCart, RefreshCw, Loader2, Search, Filter,
  ArrowUpDown, TrendingUp, TrendingDown, Package, Hash,
  ChevronDown, ChevronUp, Check, X, AlertTriangle, Trophy, Save,
  History, Bookmark, BookmarkPlus, Trash2,
} from 'lucide-react';
import CompareCycleHistoriqueTab from './CompareCycleHistoriqueTab';

const GRADES = ['A', 'A-', 'B', 'C', 'C-', 'D', 'D-'];

const REAL_STATUS = (t) => {
  if (t.status === 'Reprise annulée') return 'annulee';
  if (t.status === 'Non attribuée') return 'non_attribuee';
  if (t.status === 'Saisie') return 'promesse';
  // Reprise mise à disposition
  if (!t.availability_date) return 'pret';
  // Has availability_date: check if this week or before
  try {
    const parts = t.availability_date.split('/');
    const availDate = new Date(parts[2], parts[1] - 1, parts[0]);
    const now = new Date();
    const monday = new Date(now);
    monday.setDate(now.getDate() - now.getDay() + (now.getDay() === 0 ? -6 : 1));
    monday.setHours(0, 0, 0, 0);
    if (availDate >= monday) return 'pret'; // This week = not yet shipped
  } catch {}
  return 'expedie'; // Before this week = already shipped
};

const REAL_STATUS_LABELS = {
  promesse: { label: 'Promesse', color: 'bg-blue-100 text-blue-700', desc: 'En attente reception CC' },
  pret: { label: 'Pret a expedier', color: 'bg-amber-100 text-amber-700', desc: 'Confirme, part vendredi' },
  expedie: { label: 'Expedie', color: 'bg-emerald-100 text-emerald-700', desc: 'Deja envoye' },
  annulee: { label: 'Annulee', color: 'bg-red-100 text-red-700', desc: '' },
  non_attribuee: { label: 'Non attribuee', color: 'bg-slate-100 text-slate-600', desc: '' },
};

function TransactionsTab({ transactions, loading }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const classified = useMemo(() => {
    const groups = { promesse: [], pret: [], expedie: [], annulee: [], non_attribuee: [] };
    for (const t of transactions) {
      const rs = REAL_STATUS(t);
      groups[rs].push(t);
    }
    return groups;
  }, [transactions]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return transactions.filter(t => {
      if (statusFilter !== 'all') {
        const rs = REAL_STATUS(t);
        if (statusFilter === 'annulee') { if (rs !== 'annulee' && rs !== 'non_attribuee') return false; }
        else if (rs !== statusFilter) return false;
      }
      if (!q) return true;
      return (t.model || '').toLowerCase().includes(q) ||
        (t.brand || '').toLowerCase().includes(q) ||
        (t.transaction_number || '').toLowerCase().includes(q) ||
        (t.original_imei || '').includes(q);
    });
  }, [transactions, search, statusFilter]);

  const totalExpedieAmount = useMemo(() => classified.expedie.reduce((s, t) => s + parseFloat(t.validated_amount || 0), 0), [classified]);
  const totalPretAmount = useMemo(() => classified.pret.reduce((s, t) => s + parseFloat(t.validated_amount || 0), 0), [classified]);
  const totalPromesseAmount = useMemo(() => classified.promesse.reduce((s, t) => s + parseFloat(t.original_amount || 0), 0), [classified]);

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-4">
      {/* Weekly dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className={`cursor-pointer border-amber-300 bg-amber-50/50 ${statusFilter === 'pret' ? 'ring-2 ring-amber-400' : ''}`} onClick={() => setStatusFilter(statusFilter === 'pret' ? 'all' : 'pret')}>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-amber-700 font-semibold flex items-center gap-1"><Package className="w-3 h-3" />Prets vendredi</p>
            <p className="text-2xl font-bold text-amber-700">{classified.pret.length}</p>
            <p className="text-[10px] text-amber-600">{totalPretAmount.toFixed(0)} EUR</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer border-blue-200 ${statusFilter === 'promesse' ? 'ring-2 ring-blue-400' : ''}`} onClick={() => setStatusFilter(statusFilter === 'promesse' ? 'all' : 'promesse')}>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-blue-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Promesses</p>
            <p className="text-2xl font-bold text-blue-600">{classified.promesse.length}</p>
            <p className="text-[10px] text-blue-500">{totalPromesseAmount.toFixed(0)} EUR</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer border-emerald-200 ${statusFilter === 'expedie' ? 'ring-2 ring-emerald-400' : ''}`} onClick={() => setStatusFilter(statusFilter === 'expedie' ? 'all' : 'expedie')}>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-emerald-600 flex items-center gap-1"><Check className="w-3 h-3" />Expedies</p>
            <p className="text-2xl font-bold text-emerald-600">{classified.expedie.length}</p>
            <p className="text-[10px] text-emerald-500">{totalExpedieAmount.toFixed(0)} EUR</p>
          </CardContent>
        </Card>
        <Card className={`cursor-pointer border-red-200 ${statusFilter === 'annulee' ? 'ring-2 ring-red-400' : ''}`} onClick={() => setStatusFilter(statusFilter === 'annulee' ? 'all' : 'annulee')}>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-red-500">Annulees / Autres</p>
            <p className="text-2xl font-bold text-red-500">{(classified.annulee.length || 0) + (classified.non_attribuee.length || 0)}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-slate-500">Total</p>
            <p className="text-2xl font-bold text-slate-800" data-testid="cc-tx-count">{transactions.length}</p>
            <p className="text-[10px] text-slate-400" data-testid="cc-tx-total">{(totalExpedieAmount + totalPretAmount + totalPromesseAmount).toFixed(0)} EUR</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher modele, IMEI, numero..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm" data-testid="cc-tx-search" />
        </div>
        <div className="flex gap-1">
          {['all', 'pret', 'promesse', 'expedie', 'annulee'].map(s => {
            const count = s === 'all' ? transactions.length : s === 'annulee' ? (classified.annulee?.length || 0) + (classified.non_attribuee?.length || 0) : classified[s]?.length || 0;
            const label = s === 'all' ? 'Tous' : s === 'annulee' ? 'Annulee' : REAL_STATUS_LABELS[s]?.label || s;
            return (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${statusFilter === s ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {label} ({count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Table */}
      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cc-tx-table">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/50">
                <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Date</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">N° Transaction</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Modele</th>
                <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">IMEI</th>
                <th className="text-center px-3 py-2 text-xs font-semibold text-slate-600">Grade</th>
                <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Montant</th>
                <th className="text-center px-3 py-2 text-xs font-semibold text-slate-600">Dispo</th>
                <th className="text-center px-3 py-2 text-xs font-semibold text-slate-600">Statut</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t, i) => {
                const rs = REAL_STATUS(t);
                const rsInfo = REAL_STATUS_LABELS[rs];
                const amount = parseFloat(t.validated_amount || t.original_amount || 0);
                const gradeChanged = t.validated_grade && t.original_grade && t.validated_grade !== t.original_grade;
                return (
                  <tr key={t.transaction_number || i} className={`border-b border-slate-100 hover:bg-slate-50/50 ${rs === 'pret' ? 'bg-amber-50/30' : ''}`} data-testid={`cc-tx-row-${i}`}>
                    <td className="px-3 py-2 text-xs text-slate-500">{t.create_date}</td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-600">{t.transaction_number}</td>
                    <td className="px-3 py-2">
                      <span className="text-xs font-medium text-slate-800">{t.brand} {t.model}</span>
                    </td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-500">{t.original_imei}</td>
                    <td className="px-3 py-2 text-center">
                      {gradeChanged ? (
                        <span className="text-[10px]">
                          <Badge variant="outline" className="text-[10px] line-through opacity-50">{t.original_grade}</Badge>
                          <span className="mx-0.5">→</span>
                          <Badge variant="outline" className="text-[10px] border-red-300 text-red-600">{t.validated_grade}</Badge>
                        </span>
                      ) : (
                        <Badge variant="outline" className="text-[10px]">{t.validated_grade || t.original_grade}</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-xs font-semibold text-emerald-700">
                      {amount.toFixed(0)} EUR
                    </td>
                    <td className="px-3 py-2 text-center text-[10px] text-slate-400">{t.availability_date || '-'}</td>
                    <td className="px-3 py-2 text-center">
                      <Badge className={`text-[10px] border-0 ${rsInfo.color}`}>{rsInfo.label}</Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function CatalogTab({ products, loading, bidHistory, onBidSent, lostPositions }) {
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('all');
  const [modelFilter, setModelFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('Mobile');
  const [storageFilter, setStorageFilter] = useState('all');
  const [onlyWithPrice, setOnlyWithPrice] = useState(false);
  const [onlyFirst, setOnlyFirst] = useState(false);
  const [onlyLost, setOnlyLost] = useState(false);
  const [sortBy, setSortBy] = useState('product');
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editPrices, setEditPrices] = useState({});
  const [saving, setSaving] = useState(false);
  // Cart: staged changes to send manually
  const [cart, setCart] = useState({}); // { idproduct: { product_name, grades: { A: 15, ... } } }
  // Mass selection
  const [selected, setSelected] = useState(new Set());
  const [massGrades, setMassGrades] = useState(new Set(['D-']));
  // Saved named selections (e.g. "ip cd")
  const [savedSelections, setSavedSelections] = useState([]);
  const [selectionMenuOpen, setSelectionMenuOpen] = useState(false);
  const [saveSelectionName, setSaveSelectionName] = useState('');
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveGrades, setSaveGrades] = useState(new Set());
  // Pagination
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);

  const { brands, types } = useMemo(() => {
    const b = {}, t = {};
    for (const p of products) {
      b[p.manufacturer] = (b[p.manufacturer] || 0) + 1;
      t[p.type] = (t[p.type] || 0) + 1;
    }
    return { brands: b, types: t };
  }, [products]);

  // Models filtered by selected brand
  const models = useMemo(() => {
    const m = {};
    for (const p of products) {
      if (brandFilter !== 'all' && p.manufacturer !== brandFilter) continue;
      if (typeFilter !== 'all' && p.type !== typeFilter) continue;
      const model = p.product_model || p.product || '';
      if (model) m[model] = (m[model] || 0) + 1;
    }
    return m;
  }, [products, brandFilter, typeFilter]);

  // Extract storage capacity from product name (e.g. "iPhone 14 Pro 128Go" -> "128Go")
  const extractStorage = (name) => {
    if (!name) return null;
    const m = name.match(/(\d+)\s*(Go|To|GB|TB)\b/i);
    if (!m) return null;
    let val = parseInt(m[1]);
    let unit = m[2].toLowerCase();
    // Normalize: 1To = 1024Go, 1TB = 1024GB
    if (unit === 'to' || unit === 'tb') val *= 1024;
    return `${val}Go`;
  };

  // Storages filtered by selected brand+model
  const storages = useMemo(() => {
    const s = {};
    for (const p of products) {
      if (brandFilter !== 'all' && p.manufacturer !== brandFilter) continue;
      if (typeFilter !== 'all' && p.type !== typeFilter) continue;
      if (modelFilter !== 'all' && (p.product_model || '') !== modelFilter) continue;
      const storage = extractStorage(p.product);
      if (storage) s[storage] = (s[storage] || 0) + 1;
    }
    return s;
  }, [products, brandFilter, typeFilter, modelFilter]);

  // Reset model filter when brand changes
  const handleBrandChange = (val) => {
    setBrandFilter(val);
    setModelFilter('all');
    setStorageFilter('all');
    setPage(1);
  };

  const lostIds = useMemo(() => new Set(lostPositions.map(l => l.idproduct)), [lostPositions]);
  const lostByProduct = useMemo(() => {
    const map = {};
    for (const l of lostPositions) {
      if (!map[l.idproduct]) map[l.idproduct] = [];
      map[l.idproduct].push(l);
    }
    return map;
  }, [lostPositions]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return products.filter(p => {
      if (typeFilter !== 'all' && p.type !== typeFilter) return false;
      if (brandFilter !== 'all' && p.manufacturer !== brandFilter) return false;
      if (modelFilter !== 'all' && (p.product_model || '') !== modelFilter) return false;
      if (storageFilter !== 'all' && extractStorage(p.product) !== storageFilter) return false;
      if (onlyWithPrice && parseFloat(p['price grade A'] || 0) === 0) return false;
      if (onlyFirst && !GRADES.some(g => parseFloat(p[`price grade ${g}`] || 0) > 0 && p[`rank grade ${g}`] === 1)) return false;
      if (onlyLost && !lostIds.has(p.idproduct)) return false;
      if (!q) return true;
      return (p.product || '').toLowerCase().includes(q) ||
        (p.manufacturer || '').toLowerCase().includes(q) ||
        (p.product_model || '').toLowerCase().includes(q);
    }).sort((a, b) => {
      if (sortBy === 'product') return (a.product || '').localeCompare(b.product || '');
      if (sortBy === 'rank') return (a['rank grade A'] || 99) - (b['rank grade A'] || 99);
      if (sortBy === 'price') return parseFloat(b['best price grade A'] || 0) - parseFloat(a['best price grade A'] || 0);
      return 0;
    });
  }, [products, search, brandFilter, modelFilter, typeFilter, storageFilter, onlyWithPrice, onlyFirst, onlyLost, sortBy, lostIds]);

  const statsWithPrice = useMemo(() => products.filter(p => parseFloat(p['price grade A'] || 0) > 0).length, [products]);
  const statsFirst = useMemo(() => products.filter(p => GRADES.some(g => parseFloat(p[`price grade ${g}`] || 0) > 0 && p[`rank grade ${g}`] === 1)).length, [products]);

  const startEdit = (p) => {
    const prices = {};
    // If already in cart, use cart values
    if (cart[p.idproduct]) {
      for (const g of GRADES) prices[g] = String(cart[p.idproduct].grades[g] || p[`price grade ${g}`] || '0');
    } else {
      for (const g of GRADES) prices[g] = p[`price grade ${g}`] || '0';
    }
    setEditPrices(prices);
    setEditingId(p.idproduct);
  };

  // +1 EUR above best price for a grade
  const setBestPlus1 = (grade, p) => {
    const best = parseFloat(p[`best price grade ${grade}`] || 0);
    setEditPrices(prev => ({ ...prev, [grade]: String(best + 1) }));
  };

  // Apply max of lower grades to all higher grades
  // GRADES order: A, A-, B, C, C-, D, D- (index 0=highest, 6=lowest)
  // We go from D- (lowest) upward, tracking the max seen
  const propagateUp = () => {
    const newPrices = { ...editPrices };
    let maxSeen = 0;
    // Walk from D- to A (reverse order)
    for (let i = GRADES.length - 1; i >= 0; i--) {
      const g = GRADES[i];
      const val = parseFloat(newPrices[g] || 0);
      if (val > maxSeen) maxSeen = val;
      if (val < maxSeen) newPrices[g] = String(maxSeen);
    }
    setEditPrices(newPrices);
    toast.success('Prix propages aux grades superieurs');
  };

  // Add to cart (stage changes)
  const addToCart = (p) => {
    const grades = {};
    for (const g of GRADES) grades[g] = parseFloat(editPrices[g] || 0);
    // Validate: all grades must have at least the max of lower grades
    let maxSeen = 0;
    let valid = true;
    for (let i = GRADES.length - 1; i >= 0; i--) {
      if (grades[GRADES[i]] < maxSeen) { valid = false; break; }
      if (grades[GRADES[i]] > maxSeen) maxSeen = grades[GRADES[i]];
    }
    if (!valid) {
      toast.error('Les grades superieurs doivent avoir un prix >= aux grades inferieurs. Utilisez "Propager" pour corriger.');
      return;
    }
    setCart(prev => ({ ...prev, [p.idproduct]: { product_name: `${p.manufacturer} ${p.product}`, grades } }));
    setEditingId(null);
    toast.success(`${p.manufacturer} ${p.product} ajoute au panier`);
  };

  const removeFromCart = (id) => {
    setCart(prev => { const n = { ...prev }; delete n[id]; return n; });
  };

  // Send all cart items at once
  const sendCart = async () => {
    if (Object.keys(cart).length === 0) { toast.error('Panier vide'); return; }
    setSaving(true);
    try {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const applyDate = tomorrow.toISOString().split('T')[0];
      const productList = Object.entries(cart).map(([id, item]) => {
        const grades = {};
        const allZero = Object.values(item.grades).every(v => parseFloat(v || 0) === 0);
        for (const g of GRADES) {
          const val = parseFloat(item.grades[g] || 0);
          if (allZero) {
            // Liberation: send 0 explicitly for all grades
            grades[g] = 0;
          } else if (val > 0) {
            grades[g] = val;
          }
        }
        return { idproduct: parseInt(id), grades, apply_date: applyDate };
      });
      const res = await setCompareCyclePrices(productList);
      if (res.result_code === 'OK') {
        const results = res.results || {};
        if (results.warnings?.length) {
          for (const w of results.warnings.slice(0, 5)) {
            toast.info(`Confirmation requise: produit ${w.idproduct}`, { description: w.message, duration: 10000 });
          }
        }
        if (results.errors?.length) {
          for (const e of results.errors.slice(0, 5)) toast.error(`Erreur: ${e.error || e.grade || JSON.stringify(e)}`);
          if (results.errors.length > 5) toast.error(`+ ${results.errors.length - 5} autres erreurs`);
        }
        toast.success(res.message || `${productList.length} produit(s) mis a jour !`);
        if (!results.errors?.length) {
          setCart({});
          if (onBidSent) onBidSent();
        }
      } else {
        toast.error(res.message || 'Erreur CompareCycle');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur envoi'); }
    finally { setSaving(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const selectAllFiltered = () => {
    const start = (page - 1) * perPage;
    const ids = new Set(filtered.slice(start, start + perPage).map(p => p.idproduct));
    setSelected(prev => prev.size === ids.size && [...ids].every(id => prev.has(id)) ? new Set() : ids);
  };
  const isAllSelected = (() => {
    const start = (page - 1) * perPage;
    const pageItems = filtered.slice(start, start + perPage);
    return pageItems.length > 0 && pageItems.every(p => selected.has(p.idproduct));
  })();

  // Load saved selections on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await getCompareCycleSelections();
        setSavedSelections(r.selections || []);
      } catch { /* silent */ }
    })();
  }, []);

  const handleSaveSelection = async () => {
    const name = saveSelectionName.trim();
    if (!name) { toast.error('Nom requis'); return; }
    if (selected.size === 0) { toast.error('Aucun produit sélectionné'); return; }
    try {
      const ids = [...selected].map(id => String(id));
      const grades = GRADES.filter(g => saveGrades.has(g));
      const r = await saveCompareCycleSelection(name, ids, grades);
      const fresh = await getCompareCycleSelections();
      setSavedSelections(fresh.selections || []);
      if (grades.length > 0) setMassGrades(new Set(grades));
      toast.success(`Sélection "${name}" ${r.replaced ? 'mise à jour' : 'créée'} (${r.count} produits${grades.length ? ` · grades ${grades.join(', ')}` : ''})`);
      setShowSaveModal(false);
      setSaveSelectionName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur sauvegarde');
    }
  };

  const handleLoadSelection = (sel) => {
    // sel.product_ids are strings; products[].idproduct may be number — match both
    const idsSet = new Set(sel.product_ids.map(String));
    const matched = products.filter(p => idsSet.has(String(p.idproduct))).map(p => p.idproduct);
    setSelected(new Set(matched));
    if (sel.grades && sel.grades.length > 0) setMassGrades(new Set(sel.grades));
    setSelectionMenuOpen(false);
    toast.success(`"${sel.name}" chargée : ${matched.length}/${sel.count} produits${sel.grades?.length ? ` · grades ${sel.grades.join(', ')}` : ''}`);
  };

  const handleDeleteSelection = async (name, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Supprimer la sélection "${name}" ?`)) return;
    try {
      await deleteCompareCycleSelection(name);
      const fresh = await getCompareCycleSelections();
      setSavedSelections(fresh.selections || []);
      toast.success(`"${name}" supprimée`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur suppression');
    }
  };

  // Mass +N EUR on selected grades, propagate to higher grades
  // Incremental: each click adds +N more from best
  const massPlusN = (n = 1) => {
    if (selected.size === 0) { toast.error('Aucun produit selectionne'); return; }
    if (massGrades.size === 0) { toast.error('Selectionnez au moins un grade'); return; }
    const selectedProducts = products.filter(p => selected.has(p.idproduct));
    let count = 0;
    const newCart = { ...cart };
    for (const p of selectedProducts) {
      const existing = newCart[p.idproduct]?.grades || {};
      const grades = {};
      for (const g of GRADES) grades[g] = existing[g] !== undefined ? existing[g] : parseFloat(p[`price grade ${g}`] || 0);
      for (const mg of massGrades) {
        const best = parseFloat(p[`best price grade ${mg}`] || 0);
        const currentCartVal = existing[mg];
        if (currentCartVal !== undefined && currentCartVal > best) {
          grades[mg] = currentCartVal + n;
        } else {
          grades[mg] = best + n;
        }
      }
      let maxSeen = 0;
      for (let i = GRADES.length - 1; i >= 0; i--) {
        if (grades[GRADES[i]] > maxSeen) maxSeen = grades[GRADES[i]];
        if (grades[GRADES[i]] < maxSeen) grades[GRADES[i]] = maxSeen;
      }
      newCart[p.idproduct] = { product_name: `${p.manufacturer} ${p.product}`, grades };
      count++;
    }
    setCart(newCart);
    const gradesLabel = GRADES.filter(g => massGrades.has(g)).join(', ');
    toast.success(`${count} produit(s) : +${n}€ appliqué sur ${gradesLabel} (cumulable à chaque clic) et propagé`);
  };

  const toggleMassGrade = (g) => {
    setMassGrades(prev => { const n = new Set(prev); n.has(g) ? n.delete(g) : n.add(g); return n; });
  };

  // Mass release: set all grades to 0 for selected products
  const massRelease = () => {
    if (selected.size === 0) { toast.error('Aucun produit selectionne'); return; }
    const selectedProducts = products.filter(p => selected.has(p.idproduct));
    const newCart = { ...cart };
    for (const p of selectedProducts) {
      const grades = {};
      for (const g of GRADES) grades[g] = 0;
      newCart[p.idproduct] = { product_name: `${p.manufacturer} ${p.product}`, grades };
    }
    setCart(newCart);
    toast.success(`${selectedProducts.length} produit(s) mis a 0 (liberation)`);
  };

  // Mass reposition on LOST grades only: best+1 on each lost grade, propagate up
  // Incremental: each click adds +1 more
  const massRepositionLost = () => {
    if (selected.size === 0) { toast.error('Aucun produit selectionne'); return; }
    const selectedProducts = products.filter(p => selected.has(p.idproduct));
    let count = 0;
    const newCart = { ...cart };
    for (const p of selectedProducts) {
      const lost = lostByProduct[p.idproduct];
      if (!lost || lost.length === 0) continue;
      const existing = newCart[p.idproduct]?.grades || {};
      const grades = {};
      for (const g of GRADES) grades[g] = existing[g] !== undefined ? existing[g] : parseFloat(p[`price grade ${g}`] || 0);
      for (const l of lost) {
        const g = l.grade;
        const best = parseFloat(p[`best price grade ${g}`] || 0);
        const currentCartVal = existing[g];
        if (currentCartVal !== undefined && currentCartVal > best) {
          grades[g] = currentCartVal + 1;
        } else {
          grades[g] = best + 1;
        }
      }
      // Propagate up
      let maxSeen = 0;
      for (let i = GRADES.length - 1; i >= 0; i--) {
        if (grades[GRADES[i]] > maxSeen) maxSeen = grades[GRADES[i]];
        if (grades[GRADES[i]] < maxSeen) grades[GRADES[i]] = maxSeen;
      }
      newCart[p.idproduct] = { product_name: `${p.manufacturer} ${p.product}`, grades };
      count++;
    }
    setCart(newCart);
    toast.success(`${count} produit(s) repositionnes sur les grades perdus`);
  };

  // Quick +1 on a single grade from the list view
  // First click = best price + 1, each additional click = +1 more
  const quickPlus1 = (p, grade) => {
    const existing = cart[p.idproduct]?.grades || {};
    const grades = {};
    for (const g of GRADES) grades[g] = existing[g] !== undefined ? existing[g] : parseFloat(p[`price grade ${g}`] || 0);
    const best = parseFloat(p[`best price grade ${grade}`] || 0);
    const currentCartVal = existing[grade];
    if (currentCartVal !== undefined && currentCartVal > best) {
      // Already modified: increment from current cart value
      grades[grade] = currentCartVal + 1;
    } else {
      // First click: best + 1
      grades[grade] = best + 1;
    }
    // Propagate up: ensure higher grades >= max of lower grades
    let maxSeen = 0;
    for (let i = GRADES.length - 1; i >= 0; i--) {
      if (grades[GRADES[i]] > maxSeen) maxSeen = grades[GRADES[i]];
      if (grades[GRADES[i]] < maxSeen) grades[GRADES[i]] = maxSeen;
    }
    setCart(prev => ({ ...prev, [p.idproduct]: { product_name: `${p.manufacturer} ${p.product}`, grades } }));
  };

  if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>;

  return (
    <div className="space-y-4">
      {/* Save Selection Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowSaveModal(false)}>
          <div className="bg-white rounded-lg p-5 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-slate-800 mb-3">Sauvegarder la sélection</h3>
            <p className="text-xs text-slate-500 mb-3">{selected.size} produit(s) sélectionné(s). Donnez un nom à cette sélection.</p>
            <Input
              autoFocus
              placeholder="Ex: ip cd, iphones pro, samsung A series..."
              value={saveSelectionName}
              onChange={e => setSaveSelectionName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveSelection(); if (e.key === 'Escape') setShowSaveModal(false); }}
              className="h-9 text-sm"
              data-testid="cc-save-selection-name"
            />
            <div className="mt-3">
              <p className="text-xs font-medium text-slate-600 mb-1.5">Grades de la sélection (pour le +1/+2/+3 rapide) :</p>
              <div className="flex items-center gap-1 flex-wrap">
                {GRADES.map(g => (
                  <button key={g} type="button"
                    onClick={() => setSaveGrades(prev => { const n = new Set(prev); n.has(g) ? n.delete(g) : n.add(g); return n; })}
                    data-testid={`cc-save-grade-${g}`}
                    className={`px-2 py-1 rounded text-[11px] font-semibold transition-colors ${saveGrades.has(g) ? 'bg-amber-200 text-amber-900 ring-1 ring-amber-400' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                    {g}
                  </button>
                ))}
              </div>
            </div>
            {savedSelections.some(s => s.name === saveSelectionName.trim()) && (
              <p className="text-[11px] text-amber-600 mt-2">Ce nom existe déjà — il sera remplacé.</p>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => { setShowSaveModal(false); setSaveSelectionName(''); }} data-testid="cc-save-selection-cancel">Annuler</Button>
              <Button size="sm" onClick={handleSaveSelection} className="bg-emerald-600 hover:bg-emerald-700" data-testid="cc-save-selection-confirm">
                <BookmarkPlus className="w-3.5 h-3.5 mr-1" />Sauvegarder
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="border-slate-200"><CardContent className="pt-4 pb-3">
          <p className="text-xs text-slate-500">Produits catalogue</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="cc-cat-count">{products.length}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="pt-4 pb-3">
          <p className="text-xs text-slate-500">Avec vos prix</p>
          <p className="text-2xl font-bold text-emerald-600">{statsWithPrice}</p>
        </CardContent></Card>
        <Card className="border-yellow-200 bg-yellow-50/30 cursor-pointer" onClick={() => { setOnlyFirst(!onlyFirst); setOnlyLost(false); setOnlyWithPrice(true); }}>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-yellow-700 flex items-center gap-1"><Trophy className="w-3 h-3" />1ere position</p>
            <p className="text-2xl font-bold text-yellow-700" data-testid="cc-cat-first">{statsFirst}</p>
          </CardContent>
        </Card>
        <Card className={`border-red-200 bg-red-50/30 cursor-pointer ${onlyLost ? 'ring-2 ring-red-400' : ''}`} onClick={() => { setOnlyLost(!onlyLost); setOnlyFirst(false); setOnlyWithPrice(true); }}>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs text-red-600 flex items-center gap-1"><TrendingDown className="w-3 h-3" />Positions perdues</p>
            <p className="text-2xl font-bold text-red-600" data-testid="cc-cat-lost">{lostPositions.length}</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200"><CardContent className="pt-4 pb-3">
          <p className="text-xs text-slate-500">Marques</p>
          <p className="text-2xl font-bold text-slate-800">{Object.keys(brands).length}</p>
        </CardContent></Card>
        <Card className="border-slate-200"><CardContent className="pt-4 pb-3">
          <p className="text-xs text-slate-500">Categories</p>
          <p className="text-2xl font-bold text-slate-800">{Object.keys(types).length}</p>
        </CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2 w-4 h-4 text-slate-400" />
          <Input placeholder="Rechercher produit..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm" data-testid="cc-cat-search" />
        </div>
        <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
          className="h-8 text-xs border rounded-md px-2 bg-white" data-testid="cc-cat-type-filter">
          <option value="all">Tous types</option>
          {Object.entries(types).sort((a, b) => b[1] - a[1]).map(([t, c]) => (
            <option key={t} value={t}>{t} ({c})</option>
          ))}
        </select>
        <select value={brandFilter} onChange={e => handleBrandChange(e.target.value)}
          className="h-8 text-xs border rounded-md px-2 bg-white" data-testid="cc-cat-brand-filter">
          <option value="all">Toutes marques</option>
          {Object.entries(brands).sort((a, b) => b[1] - a[1]).map(([b, c]) => (
            <option key={b} value={b}>{b} ({c})</option>
          ))}
        </select>
        <select value={modelFilter} onChange={e => { setModelFilter(e.target.value); setStorageFilter('all'); setPage(1); }}
          className="h-8 text-xs border rounded-md px-2 bg-white max-w-[200px]" data-testid="cc-cat-model-filter">
          <option value="all">Tous modeles ({Object.keys(models).length})</option>
          {Object.entries(models).sort((a, b) => a[0].localeCompare(b[0])).map(([m, c]) => (
            <option key={m} value={m}>{m} ({c})</option>
          ))}
        </select>
        <select value={storageFilter} onChange={e => { setStorageFilter(e.target.value); setPage(1); }}
          className="h-8 text-xs border rounded-md px-2 bg-white" data-testid="cc-cat-storage-filter">
          <option value="all">Toutes capacites ({Object.keys(storages).length})</option>
          {Object.entries(storages).sort((a, b) => parseInt(a[0]) - parseInt(b[0])).map(([s, c]) => (
            <option key={s} value={s}>{s} ({c})</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" checked={onlyWithPrice} onChange={e => { setOnlyWithPrice(e.target.checked); if (!e.target.checked) setOnlyFirst(false); }} className="rounded" />
          Mes prix uniquement
        </label>
        <label className="flex items-center gap-1.5 text-xs text-yellow-700 cursor-pointer font-medium">
          <input type="checkbox" checked={onlyFirst} onChange={e => { setOnlyFirst(e.target.checked); if (e.target.checked) { setOnlyWithPrice(true); setOnlyLost(false); } }} className="rounded" />
          1ere position
        </label>
        <label className="flex items-center gap-1.5 text-xs text-red-600 cursor-pointer font-medium">
          <input type="checkbox" checked={onlyLost} onChange={e => { setOnlyLost(e.target.checked); if (e.target.checked) { setOnlyWithPrice(true); setOnlyFirst(false); } }} className="rounded" />
          Positions perdues ({lostPositions.length})
        </label>
        <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="h-8 text-xs border rounded-md px-2 bg-white">
          <option value="product">Tri: Nom</option>
          <option value="rank">Tri: Classement</option>
          <option value="price">Tri: Meilleur prix</option>
        </select>
      </div>

      {/* Pagination info + per page selector */}
      {(() => {
        const totalPages = Math.ceil(filtered.length / perPage);
        const start = (page - 1) * perPage;
        const paged = filtered.slice(start, start + perPage);
        const currentPage = Math.min(page, totalPages || 1);
        return (<>
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">{filtered.length} produits — Page {currentPage}/{totalPages || 1}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Afficher</span>
          <select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }} className="h-7 text-xs border rounded px-1.5 bg-white">
            {[25, 50, 100, 200].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="text-xs text-slate-500">par page</span>
        </div>
      </div>

      {/* Mass action bar */}
      <Card className="border-slate-300 bg-slate-50/80">
        <CardContent className="py-2 px-3">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
              <input type="checkbox" checked={isAllSelected} onChange={selectAllFiltered} className="rounded" />
              Page ({Math.min(perPage, filtered.length - (page - 1) * perPage)})
            </label>
            {/* Saved selections menu */}
            <div className="relative">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                onClick={() => setSelectionMenuOpen(v => !v)}
                data-testid="cc-saved-selections-btn"
              >
                <Bookmark className="w-3 h-3" />
                Mes sélections ({savedSelections.length})
                <ChevronDown className="w-3 h-3" />
              </Button>
              {selectionMenuOpen && (
                <div className="absolute z-20 top-full mt-1 left-0 w-80 bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-y-auto"
                  onMouseLeave={() => setSelectionMenuOpen(false)}
                  data-testid="cc-saved-selections-menu">
                  {savedSelections.length === 0 ? (
                    <div className="p-3 text-xs text-slate-400 text-center">Aucune sélection sauvegardée</div>
                  ) : (
                    savedSelections.map(sel => (
                      <div key={sel.name}
                        className="flex items-center justify-between px-3 py-2 hover:bg-emerald-50 cursor-pointer border-b border-slate-100 last:border-0"
                        onClick={() => handleLoadSelection(sel)}
                        data-testid={`cc-saved-selection-${sel.name}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Bookmark className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <span className="text-sm font-medium text-slate-800 truncate">{sel.name}</span>
                          <Badge variant="outline" className="text-[10px] shrink-0">{sel.count}</Badge>
                          {sel.grades?.length > 0 && (
                            <span className="text-[10px] font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5 shrink-0">{sel.grades.join(' ')}</span>
                          )}
                        </div>
                        <button
                          onClick={(e) => handleDeleteSelection(sel.name, e)}
                          className="p-1 text-slate-300 hover:text-red-500 shrink-0"
                          title="Supprimer">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {selected.size > 0 && (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs gap-1 border-emerald-300 text-emerald-700 bg-white hover:bg-emerald-50"
                  onClick={() => { setSaveGrades(new Set(massGrades)); setShowSaveModal(true); }}
                  data-testid="cc-save-selection-btn">
                  <BookmarkPlus className="w-3 h-3" />Sauver ({selected.size})
                </Button>
                <span className="text-xs font-semibold text-slate-700">{selected.size} selectionne(s)</span>
                <span className="text-slate-300">|</span>
                <div className="flex items-center gap-1">
                  {GRADES.map(g => (
                    <button key={g} onClick={() => toggleMassGrade(g)}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-semibold transition-colors ${massGrades.has(g) ? 'bg-amber-200 text-amber-900 ring-1 ring-amber-400' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                      {g}
                    </button>
                  ))}
                </div>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100" onClick={() => massPlusN(1)} disabled={massGrades.size === 0} data-testid="cc-mass-plus1">
                  <TrendingUp className="w-3 h-3" />Best+1 sur {[...massGrades].join(', ') || '...'}
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100" onClick={() => massPlusN(2)} disabled={massGrades.size === 0} data-testid="cc-mass-plus2">
                  +2
                </Button>
                <Button size="sm" variant="outline" className="h-7 text-xs px-2 text-amber-700 border-amber-300 bg-amber-50 hover:bg-amber-100" onClick={() => massPlusN(3)} disabled={massGrades.size === 0} data-testid="cc-mass-plus3">
                  +3
                </Button>
                <span className="text-slate-300">|</span>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-600 border-red-300 bg-red-50 hover:bg-red-100" onClick={massRelease} data-testid="cc-mass-release">
                  <X className="w-3 h-3" />Liberer
                </Button>
                {lostPositions.length > 0 && (<>
                  <span className="text-slate-300">|</span>
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-700 border-red-400 bg-red-50 hover:bg-red-100 font-semibold" onClick={massRepositionLost} data-testid="cc-mass-reposition">
                    <TrendingDown className="w-3 h-3" />Repositionner perdus +1
                  </Button>
                </>)}
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Cart bar */}
      {Object.keys(cart).length > 0 && (
        <Card className="border-blue-300 bg-blue-50/50" data-testid="cc-cart-bar">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-semibold text-blue-800">Panier: {Object.keys(cart).length} produit(s) a envoyer</span>
                <div className="flex gap-1 flex-wrap">
                  {Object.entries(cart).map(([id, item]) => (
                    <Badge key={id} variant="outline" className="text-[9px] bg-white gap-1">
                      {item.product_name}
                      <button onClick={() => removeFromCart(parseInt(id))} className="text-red-400 hover:text-red-600 ml-0.5"><X className="w-2.5 h-2.5" /></button>
                    </Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => setCart({})}>Vider</Button>
                <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700 gap-1" onClick={sendCart} disabled={saving} data-testid="cc-send-cart-btn">
                  <Save className="w-3 h-3" />{saving ? 'Envoi en cours...' : `Envoyer ${Object.keys(cart).length} modification(s)`}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Product list */}
      <div className="space-y-1.5">
        {paged.map(p => {
          const myPriceA = parseFloat(p['price grade A'] || 0);
          const bestA = parseFloat(p['best price grade A'] || 0);
          const rankA = p['rank grade A'] || 0;
          const hasPrice = myPriceA > 0;
          const isExpanded = expandedId === p.idproduct;
          const isEditing = editingId === p.idproduct;
          const firstGrades = GRADES.filter(g => parseFloat(p[`price grade ${g}`] || 0) > 0 && p[`rank grade ${g}`] === 1);
          const inCart = !!cart[p.idproduct];
          const bid = bidHistory[String(p.idproduct)];
          const hasHistory = !hasPrice && bid;

          return (
            <Card key={p.idproduct} className={`border-slate-200 ${inCart ? 'border-blue-300 bg-blue-50/40' : hasPrice ? 'bg-emerald-50/30' : ''}`} data-testid={`cc-product-${p.idproduct}`}>
              <div className="px-3 py-2">
                <div className="flex items-center gap-2 mb-1.5">
                  <input type="checkbox" checked={selected.has(p.idproduct)} onChange={() => toggleSelect(p.idproduct)} className="rounded shrink-0" onClick={e => e.stopPropagation()} />
                  <div className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : p.idproduct)}>
                    <span className="text-xs font-semibold text-slate-800 truncate">{p.manufacturer} {p.product}</span>
                    <Badge variant="outline" className="text-[9px] shrink-0">{p.type}</Badge>
                    {inCart && <Badge className="text-[8px] border-0 bg-blue-100 text-blue-700 px-1.5 py-0"><ShoppingCart className="w-2 h-2 mr-0.5 inline" />Panier</Badge>}
                    {p.apply_date && <span className="text-[9px] text-slate-400">{p.apply_date}</span>}
                    {hasHistory && (
                      <span className="text-[9px] text-orange-500 italic">Derniere mise: {Math.max(...Object.values(bid.grades).map(Number))} EUR le {new Date(bid.date).toLocaleDateString('fr-FR')}</span>
                    )}
                  </div>
                  <div className="cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : p.idproduct)}>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </div>
                </div>
                {/* Compact grade grid */}
                <div className="flex items-center gap-1 ml-6">
                  {GRADES.map(g => {
                    const my = parseFloat(p[`price grade ${g}`] || 0);
                    const best = parseFloat(p[`best price grade ${g}`] || 0);
                    const rank = p[`rank grade ${g}`] || 0;
                    const isFirst = my > 0 && rank === 1;
                    // Show cart price if in cart
                    const cartPrice = cart[p.idproduct]?.grades?.[g];
                    const displayPrice = cartPrice !== undefined ? cartPrice : my;
                    const isModified = cartPrice !== undefined && cartPrice !== my;
                    const lostGrade = (lostByProduct[p.idproduct] || []).find(l => l.grade === g);
                    return (
                      <div key={g} className={`flex-1 text-center py-1.5 px-1 rounded ${lostGrade ? 'bg-red-50 ring-1 ring-red-300' : isModified ? 'bg-blue-50 ring-1 ring-blue-300' : isFirst ? 'bg-yellow-100 ring-1 ring-yellow-300' : my > 0 ? 'bg-emerald-50' : 'bg-slate-50'}`}>
                        <div className="text-[10px] font-semibold text-slate-500">{g}</div>
                        <div className="flex items-center justify-center gap-1">
                          <span className={`text-sm font-bold ${isModified ? 'text-blue-700' : isFirst ? 'text-yellow-800' : my > 0 ? 'text-emerald-700' : 'text-slate-300'}`}>{displayPrice > 0 ? displayPrice : '-'}</span>
                          <button onClick={e => { e.stopPropagation(); quickPlus1(p, g); }}
                            className="px-1.5 py-0.5 rounded-md bg-amber-200 hover:bg-amber-300 text-amber-900 text-[11px] font-bold transition-colors leading-none" title={`+1 EUR sur ${g}`}>
                            +1
                          </button>
                        </div>
                        <div className="text-[11px] font-bold text-red-500 border border-red-200 rounded px-1 mt-0.5">{best > 0 ? best : '-'}</div>
                        {displayPrice > 0 && <div className={`text-[9px] font-semibold ${isModified ? 'text-blue-600' : isFirst ? 'text-yellow-700' : rank <= 3 ? 'text-emerald-600' : 'text-slate-400'}`}>{isModified ? 'modif' : `#${rank}`}</div>}
                        {lostGrade && <div className="text-[8px] font-bold text-red-600">perdu</div>}
                      </div>
                    );
                  })}
                </div>
              </div>

              {isExpanded && (
                <div className="px-3 pb-3 border-t border-slate-100 pt-2">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-slate-500">
                        <th className="text-left py-1 font-medium">Grade</th>
                        <th className="text-right py-1 font-medium">Mon prix</th>
                        {isEditing && <th className="text-center py-1 font-medium w-16"></th>}
                        <th className="text-center py-1 font-medium">Rang</th>
                        <th className="text-right py-1 font-medium">Meilleur</th>
                        <th className="text-right py-1 font-medium">Suivant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {GRADES.map(g => {
                        const my = parseFloat(p[`price grade ${g}`] || 0);
                        const best = parseFloat(p[`best price grade ${g}`] || 0);
                        const next = parseFloat(p[`next price grade ${g}`] || 0);
                        const rank = p[`rank grade ${g}`] || 0;
                        const editVal = parseFloat(editPrices[g] || 0);
                        const changed = isEditing && editVal !== my;
                        return (
                          <tr key={g} className={`border-t border-slate-50 ${changed ? 'bg-blue-50/50' : ''}`}>
                            <td className="py-1.5 font-semibold text-slate-700">{g}</td>
                            <td className="py-1.5 text-right">
                              {isEditing ? (
                                <Input type="number" value={editPrices[g] || ''} onChange={e => setEditPrices(prev => ({ ...prev, [g]: e.target.value }))}
                                  className={`h-6 w-20 text-xs text-right ml-auto ${changed ? 'border-blue-400 bg-blue-50' : ''}`} />
                              ) : (
                                <span className={my > 0 ? 'font-semibold text-emerald-700' : 'text-slate-300'}>{my > 0 ? `${my} EUR` : '-'}</span>
                              )}
                            </td>
                            {isEditing && (
                              <td className="py-1.5 text-center">
                                <button onClick={() => setBestPlus1(g, p)} title={`Meilleur (${best}) + 1 EUR`}
                                  className="px-1.5 py-0.5 rounded bg-amber-100 hover:bg-amber-200 text-amber-800 text-[9px] font-semibold transition-colors">
                                  +1
                                </button>
                              </td>
                            )}
                            <td className="py-1.5 text-center">
                              <Badge className={`text-[9px] border-0 ${rank === 1 ? 'bg-yellow-100 text-yellow-800' : rank <= 3 ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                                #{rank}
                              </Badge>
                            </td>
                            <td className="py-1.5 text-right font-medium text-slate-700">{best > 0 ? `${best} EUR` : '-'}</td>
                            <td className="py-1.5 text-right text-slate-500">{next > 0 ? `${next} EUR` : '-'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="flex items-center justify-between mt-2">
                    <div>
                      {isEditing && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-indigo-700 border-indigo-200" onClick={propagateUp}>
                          <ArrowUpDown className="w-3 h-3" />Propager aux grades superieurs
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {isEditing ? (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingId(null)}><X className="w-3 h-3 mr-1" />Annuler</Button>
                          <Button size="sm" className="h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={() => addToCart(p)}>
                            <ShoppingCart className="w-3 h-3 mr-1" />Ajouter au panier
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={e => { e.stopPropagation(); startEdit(p); }} data-testid={`cc-edit-${p.idproduct}`}>
                          <TrendingUp className="w-3 h-3 mr-1" />Modifier mes prix
                        </Button>
                      )}
                    </div>
                  </div>
                  {cart[p.idproduct] && !isEditing && (
                    <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200 flex items-center justify-between">
                      <span className="text-[10px] text-blue-700 font-medium">Dans le panier — Grades: {GRADES.map(g => `${g}:${cart[p.idproduct].grades[g]}`).join(', ')}</span>
                      <button onClick={() => removeFromCart(p.idproduct)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
              )}
            </Card>
          );
        })}
        {filtered.length > 100 && <p className="text-xs text-slate-400 text-center py-2">Utilisez les filtres pour affiner.</p>}
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1 pt-2">
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage <= 1} onClick={() => setPage(1)}>1</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage <= 1} onClick={() => setPage(p => p - 1)}>Prec</Button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) { pageNum = i + 1; }
            else if (currentPage <= 3) { pageNum = i + 1; }
            else if (currentPage >= totalPages - 2) { pageNum = totalPages - 4 + i; }
            else { pageNum = currentPage - 2 + i; }
            return (
              <Button key={pageNum} variant={pageNum === currentPage ? 'default' : 'outline'} size="sm"
                className={`h-7 w-7 text-xs ${pageNum === currentPage ? 'bg-emerald-600' : ''}`}
                onClick={() => setPage(pageNum)}>{pageNum}</Button>
            );
          })}
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage >= totalPages} onClick={() => setPage(p => p + 1)}>Suiv</Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" disabled={currentPage >= totalPages} onClick={() => setPage(totalPages)}>{totalPages}</Button>
        </div>
      )}
        </>); })()}
    </div>
  );
}

export default function CompareCyclePage() {
  const [activeTab, setActiveTab] = useState('transactions');
  const [transactions, setTransactions] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [loadingCat, setLoadingCat] = useState(false);
  const [configured, setConfigured] = useState(null);
  const [bidHistory, setBidHistory] = useState({});
  const [lostPositions, setLostPositions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await getCompareCycleConfig();
        setConfigured(cfg.configured);
        if (cfg.configured) loadTransactions();
      } catch { setConfigured(false); }
    })();
  }, []);

  const loadTransactions = async () => {
    setLoadingTx(true);
    try {
      const res = await getCompareCycleTransactions();
      setTransactions(res.transactions || []);
    } catch (e) { toast.error('Erreur chargement transactions'); }
    finally { setLoadingTx(false); }
  };

  const loadCatalog = async () => {
    setLoadingCat(true);
    try {
      const [res, histRes, lostRes] = await Promise.all([
        getCompareCycleCatalog(), getCompareCycleBidHistory(), getCompareCycleLostPositions()
      ]);
      setCatalog(res.products || []);
      setBidHistory(histRes.bids || {});
      setLostPositions(lostRes.lost || []);
    } catch (e) { toast.error('Erreur chargement catalogue'); }
    finally { setLoadingCat(false); }
  };

  const refreshBidHistory = async () => {
    try {
      const res = await getCompareCycleBidHistory();
      setBidHistory(res.bids || {});
    } catch {}
  };

  useEffect(() => {
    if ((activeTab === 'catalog' || activeTab === 'historique') && catalog.length === 0 && configured) loadCatalog();
  }, [activeTab, configured]);

  if (configured === null) return (
    <DashboardLayout>
      <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
    </DashboardLayout>
  );

  if (configured === false) return (
    <DashboardLayout>
      <div className="max-w-lg mx-auto py-20 text-center">
        <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-slate-800 mb-2">CompareCycle non configure</h2>
        <p className="text-sm text-slate-500">Les cles API CompareCycle ne sont pas configurees. Contactez l'administrateur.</p>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="comparecycle-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Manrope, sans-serif' }}>CompareCycle</h1>
            <p className="text-sm text-slate-500">Plateforme d'achat et encheres reconditionne</p>
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1"
            onClick={activeTab === 'transactions' ? loadTransactions : loadCatalog}
            disabled={loadingTx || loadingCat}
            data-testid="cc-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 ${(loadingTx || loadingCat) ? 'animate-spin' : ''}`} />Actualiser
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-slate-200 pb-0">
          {[
            { id: 'transactions', label: 'Transactions', icon: Package, count: transactions.length },
            { id: 'catalog', label: 'Catalogue Argus', icon: TrendingUp, count: catalog.length },
            { id: 'historique', label: 'Historique', icon: History, count: 0 },
          ].map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                activeTab === tab.id ? 'border-emerald-500 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'
              }`} data-testid={`cc-tab-${tab.id}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
              {tab.count > 0 && <Badge variant="outline" className="text-[10px] ml-1">{tab.count}</Badge>}
            </button>
          ))}
        </div>

        {activeTab === 'transactions' && <TransactionsTab transactions={transactions} loading={loadingTx} />}
        {activeTab === 'catalog' && <CatalogTab products={catalog} loading={loadingCat} bidHistory={bidHistory} onBidSent={refreshBidHistory} lostPositions={lostPositions} />}
        {activeTab === 'historique' && <CompareCycleHistoriqueTab catalog={catalog} />}
      </div>
    </DashboardLayout>
  );
}
